import outcomesData from '../../outcomes.json';

export const RIASEC_ORDER = ['R', 'I', 'A', 'S', 'E', 'C'];
export const RAPD_ORDER = ['P', 'A', 'R', 'D'];

export const riasecMeta = {
    R: {
        title: 'Realistic',
        descriptor: 'Hands-on and grounded',
        summary: 'You lean toward practical activity, tangible outcomes, and solving problems in the real world.',
        environment: 'practical settings with tools, movement, and visible output',
        motivation: 'see useful results and make things work',
        strengths: ['Build', 'Repair', 'Apply'],
        color: '#22c55e'
    },
    I: {
        title: 'Investigative',
        descriptor: 'Curious and analytical',
        summary: 'You are energized by inquiry, evidence, patterns, and understanding how systems or ideas work.',
        environment: 'analysis-rich settings where questions, patterns, and evidence matter',
        motivation: 'explore, understand, and solve deeper problems',
        strengths: ['Research', 'Analyze', 'Discover'],
        color: '#3b82f6'
    },
    A: {
        title: 'Artistic',
        descriptor: 'Expressive and original',
        summary: 'You enjoy imagination, expression, and creating new ways to communicate or shape experiences.',
        environment: 'flexible spaces that reward originality, design, and self-expression',
        motivation: 'create, interpret, and bring fresh ideas to life',
        strengths: ['Imagine', 'Design', 'Express'],
        color: '#ec4899'
    },
    S: {
        title: 'Social',
        descriptor: 'People-centered and supportive',
        summary: 'You care about helping, teaching, listening, and contributing to the growth or wellbeing of others.',
        environment: 'human-centered environments built around support, service, or learning',
        motivation: 'help people grow and make a meaningful difference',
        strengths: ['Guide', 'Support', 'Teach'],
        color: '#f97316'
    },
    E: {
        title: 'Enterprising',
        descriptor: 'Initiative-driven and persuasive',
        summary: 'You are drawn to ambition, influence, leadership, enterprise, and moving ideas or people into action.',
        environment: 'fast-moving spaces where initiative, persuasion, and leadership are valuable',
        motivation: 'lead, influence, and create forward momentum',
        strengths: ['Lead', 'Pitch', 'Mobilize'],
        color: '#8b5cf6'
    },
    C: {
        title: 'Conventional',
        descriptor: 'Structured and reliable',
        summary: 'You value order, reliability, systems, detail, and work that rewards consistency and precision.',
        environment: 'structured systems where process, detail, and dependability matter',
        motivation: 'organize well and bring clarity, accuracy, and stability',
        strengths: ['Organize', 'Systematize', 'Refine'],
        color: '#14b8a6'
    }
};

export const PART_A_CAREER_COUNSELLOR_MESSAGE = 'Contact your Yancha Career Councellor for InDepth Analysis';

const partAIntroContent = {
    analysisTitle: 'PART A',
    title: "Personality Mapping Based on John Holland’s Theory",
    paragraphs: [
        'The history of the RIASEC Occupational Interests framework dates back to a series of influential papers about vocational preferences by John Holland in the 1950s. His 1959 article in the Journal of Counseling Psychology entitled “A Theory of Vocational Choice” is particularly striking because of the way he describes the choice of a Profession/Vocation as an expression of one’s personality.',
        'Holland’s model is now often referred to as the RIASEC as this is the acronym produced by his revised names for the six categories of occupations and orientations. These are Realistic (Builder), Investigative (Intellectual), Artistic (Esthetics), Social (Supportive), Enterprising (The Persuader), and Conventional (Conforming). Each of these six types was also associated with a descriptive occupational adjective: “Doers” are realistic, “Thinkers & The Problem Solvers” are investigative, “The Creators” are artistic, “Helpers or The People Person” are social, “The Persuaders” are enterprising, and “The Organizers” are conventional.',
        'Other aspects of Holland’s original theory, such as the importance of knowledge about one’s environment and its influences on behavior, were similarly retained in later updates to the theory.'
    ],
    careerPreferencesTitle: 'Career Preferences',
    careerPreferences: [
        'Two ways of defining your strengths are looking at what you do well and what you love doing. If you love doing something, chances are you are or will become fairly proficient at it. So there is usually a connection between both factors.',
        'Psychologist John Holland believed “the choice of a vocation is an expression of personality”. His “Holland Codes”—six groupings of types of workskills—are used by the U.S. Department of Labor for classifying jobs.',
        'All people usually have some interest in all the areas, but just the top two or three are used in occupational guidance. The model used to demonstrate the Holland Codes is a hexagon. The areas next to each other are more closely related.'
    ]
};

const partATraitInference = {
    R: {
        heading: 'REALISTIC PERSONALITY (R)',
        careerHeading: 'REALISTIC (R) :: CAREER OPTIONS',
        paragraphs: [
            'A realistic personality type enjoys using their hands and eyes to explore the world and accomplish things. This individual likes doing outdoor, mechanical and physical activities and occupations. They prefer working with things, such as objects, tools, machines, plants, and animals rather than with ideas, data, and people.',
            'These personality types are confident when using their body to relate to the physical world. Mechanical and athletic abilities are a strong suit and they are usually strong and well-coordinated. A realistic personality type likes solving concrete instead of abstract problems and solves problems by doing something physical. Because of their ability with physical objects, they are often good in emergencies.',
            'A realistic personality type can deal well with the physical world, which often means they are very independent, practical minded, strong, aggressive and conservative. They may not have strong communication skills and tend to think in absolutes.',
            'A realistic personality type likes to work with animals, tools, or machines and generally avoids social activities like teaching, counseling, nursing, and informing others.'
        ]
    },
    I: {
        heading: 'INVESTIGATIVE PERSONALITY (I)',
        careerHeading: 'INVESTIGATIVE (I) :: CAREER OPTIONS',
        paragraphs: [
            'An investigative personality type tends to be analytical, intellectual and scholarly. They enjoy research, mathematical or scientific activities. These individuals live in their mind and prefer to deal with the real world from a distance. They like to read, study, use books and other data instead of working hands-on.',
            'These individuals are unconventional and independent thinkers, who are curious and very insightful. Using logic and solving highly complex, abstract problems are what these individuals enjoy most. An investigative personality type tends to focus on ideas when they are involved with people.',
            'Wherever they are, they collect information and analyze situations before making decisions. If they enjoy the outdoors, it is because they are curious, not because they enjoy rugged, heavy, physical work.',
            'An investigative personality type sees themselves as highly intelligent, but they may be introverted and lack leadership and persuasive skills. They use their mind / information to achieve, rather than their association with people and things. These individuals often avoid activities that seem mundane, commercial or enterprising.',
            'An investigative personality type prefers working in an environment that encourages scientific competencies, allows independent work and focuses on solving abstract, complex problems in original ways.'
        ]
    },
    A: {
        heading: 'ARTISTIC PERSONALITY (A)',
        careerHeading: 'ARTISTIC (A) :: CAREER OPTIONS',
        paragraphs: [
            'An artistic personality type uses their hands and mind to create new things. They appreciate beauty, unstructured activities and variety. They enjoy interesting and unusual people, sights, textures and sounds. These individuals prefer to work in unstructured situations and use their creativity and imagination.',
            'This personality type is especially sensitive to color, form, sound and feeling. They have a lively spirit and a lot of enthusiasm and can often stay focused on a creative project and forget everything around them.',
            'An artistic personality type solves problems by creating something new. Their ideas may not always please others, but opposition does not discourage them for long. These individuals are creative, impulsive, sensitive and visionary.',
            'Although they are creative, it may not necessarily be expressed with paint and canvas. Creativity can also be expressed by an artistic personality type with data and systems. They prefer to work alone and independently rather than in teams or with others.',
            'Occupations they will excel at involve working with forms, designs and patterns. These individuals often require self-expression and the work can be done without following a clear set of rules.'
        ]
    },
    S: {
        heading: 'SOCIAL PERSONALITY (S)',
        careerHeading: 'SOCIAL PERSONALITY (S) :: CAREER OPTIONS',
        paragraphs: [
            'Individuals who are a social personality type are dedicated leaders, humanistic, responsible and supportive. They use feelings, words and ideas to work with people rather than physical activity to do things. They enjoy closeness, sharing, groups, unstructured activity and being in charge.',
            'These individuals are friendly and skilled with words and solve problems by feeling. A social personality type enjoys informing, helping, training, developing and curing people in their work.',
            'Their empathy and sensitivity to emotional cues helps them solve people problems, sometimes before others are aware of them. These individuals can pull people together and generate positive energy for a good cause.',
            'People and their concerns are the focus of a social personality type. These individuals are sensitive to people’s moods and feelings, and make friends easily. They sometimes focus on people concerns to the exclusion of all else.',
            'A social personality type may appear impractical, especially to the realistic types. Their occupation will frequently involve working with, communicating with and/or teaching people. These occupations often involve helping or providing service to others.'
        ]
    },
    E: {
        heading: 'ENTERPRISING PERSONALITY (E)',
        careerHeading: 'ENTERPRISING (E) :: CAREER OPTIONS',
        paragraphs: [
            'These individuals like to work with people, influence, persuade, lead or manage for organizational goals or economic gain. An enterprising personality type is often a leader who is talented at organizing, persuading and managing. They enjoy money, power, status and being in charge.',
            'An enterprising personality type solves problems by taking risks. Instead of doing research, this person relies on intuition about what will work. They may strike an observer as restless and irresponsible since they often drop these projects after the job is underway.',
            'However, many activities would never get off the ground without the energizing influence of an enterprising personality.',
            'This personality type is an extroverted and sociable person who is energetic, enthusiastic, confident, assertive and a quick decision-maker. They often initiate projects but mostly get others to carry them out.',
            'These individuals may focus solely on a project and not notice the personal concerns of others or of themselves. Their occupation will focus on starting up and carrying out projects. These occupations can involve leading people, making many decisions, risk taking and they often deal with business.'
        ]
    },
    C: {
        heading: 'CONVENTIONAL PERSONALITY (C)',
        careerHeading: 'CONVENTIONAL (C) :: CAREER OPTIONS',
        paragraphs: [
            'A conventional personality type likes to work with data and numbers, carry out tasks in detail and follow through on the instructions of others. They are quiet, careful, responsible, well organized and task oriented. These individuals use their mind, eyes and hands to carry out tasks.',
            'This personality type solves problems by appealing to and following rules. They are task oriented and prefer to carry out tasks initiated by others, rather than being in a position of authority.',
            'Because of the attention to detail that a conventional personality type has, these individuals keep the world’s records and transmit its messages.',
            'These individuals have a strong need to feel secure and certain, follow a routine, get things finished and take care of every detail. A conventional personality type is neat, orderly and practical, and enjoys working with data (facts, figures, and numbers) in structured settings.',
            'Their occupations will frequently involve following set procedures and routines and working with data and details more than with ideas. There is usually a clear line of authority to follow.'
        ]
    }
};

export const rapdMeta = {
    A: {
        title: 'Attitude',
        descriptor: 'How you reset after setbacks',
        summary: 'This pillar looks at the emotional tone and constructive energy you bring when things do not go as planned.',
        bands: {
            high: 'You usually return to effort with constructive energy instead of staying stuck in disappointment.',
            medium: 'You recover with time and reassurance, especially when the next step is visible.',
            emerging: 'Setbacks may slow your momentum; smaller wins and support help you restart with confidence.'
        },
        growthPrompt: 'Practice naming the setback, the lesson, and the next action in one sentence.',
        color: '#f97316'
    },
    P: {
        title: 'Perspectives',
        descriptor: 'How broadly and independently you think',
        summary: 'This pillar reflects whether you can step back, reflect, and form your own considered view instead of following pressure or noise.',
        bands: {
            high: 'You tend to pause, reflect, and form your own considered view before committing to direction.',
            medium: 'You can take perspective when prompted, though outside opinions may sway you at times.',
            emerging: 'Immediate pressure or popular opinion can overshadow your own reflective judgment.'
        },
        growthPrompt: 'Slow the moment down and compare at least two viewpoints before deciding.',
        color: '#3b82f6'
    },
    R: {
        title: 'Resources',
        descriptor: 'How well you notice and use support',
        summary: 'This pillar tracks how well you notice, gather, and use tools, people, systems, and opportunities around you.',
        bands: {
            high: 'You actively notice people, tools, systems, and opportunities that can help you progress.',
            medium: 'You use support when it is visible, but may not always seek or organize it early.',
            emerging: 'Useful resources can be overlooked until someone helps you map them clearly.'
        },
        growthPrompt: 'List the people, tools, spaces, and systems that can support the goal before starting.',
        color: '#22c55e'
    },
    D: {
        title: 'Decision Making',
        descriptor: 'How thoughtfully you choose direction',
        summary: 'This pillar reflects how you compare options, weigh consequence, and commit with intention.',
        bands: {
            high: 'You are able to weigh consequences and move with intention instead of reacting impulsively.',
            medium: 'You make workable decisions, though you may hesitate when trade-offs feel unclear.',
            emerging: 'Decisions may feel heavy or reactive; clearer criteria and guided reflection help.'
        },
        growthPrompt: 'Use simple criteria: what matters most, what can wait, and what aligns with your values.',
        color: '#8b5cf6'
    }
};

const partACareerPools = {
    R: [
        'Architect',
        'Athlete',
        'Chef',
        'Computer Operator',
        'Driver',
        'Engineer',
        'Firefighter',
        'Gardener',
        'Martial Arts',
        'Paramedic',
        'Pilot',
        'Police Officer',
        'Veterinarian'
    ],
    I: [
        'Biologist',
        'Chemist',
        'Historian',
        'Researcher',
        'Doctor',
        'Mathematician',
        'Neurologist',
        'Pharmacist',
        'Physician Assistant',
        'Programmer',
        'Systems Engineer',
        'Wildlife Biologist'
    ],
    A: [
        'Actor',
        'Architect',
        'Artist',
        'Copywriter',
        'Dance Instructor',
        'English Teacher',
        'Fashion Illustrator',
        'Graphic Designer',
        'Interior Designer',
        'Intelligence Research Specialist',
        'Museum Curator',
        'Photographer'
    ],
    S: [
        'City Manager',
        'Clinical Dietitian',
        'Hospital Administrator',
        'Psychologist',
        'Insurance Claims Examiner',
        'Medical Assistant',
        'Park Naturalist',
        'Physical Therapist',
        'Police Officer',
        'Real Estate Appraiser',
        'Registered Nurse'
    ],
    E: [
        'Advertising Executive',
        'Financial Planner',
        'Business Manager',
        'Customer Service Manager',
        'Emergency Medical Personnel',
        'Entrepreneur',
        'Interpreter',
        'Lawyer / Attorney',
        'Office Manager',
        'Politician',
        'Retail Store Manager',
        'Stockbroker'
    ],
    C: [
        'Accountant',
        'Administrative Assistant',
        'Budget Analyst',
        'Claims Adjuster',
        'Computer Operator',
        'Court Reporter',
        'Editorial Assistant',
        'Primary School Teacher',
        'Insurance Underwriter',
        'Medical Records Technician',
        'Safety Inspector',
        'Tax Consultant',
        'Travel Agent'
    ]
};

const perspectiveChoiceMeta = {
    high: {
        label: 'Self-Driven',
        emphasis: 'Primary driver',
        summary: 'You tend to process choices primarily through your own insight and considered view.'
    },
    medium: {
        label: 'Seek out New',
        emphasis: 'Primary driver',
        summary: 'You tend to seek new information and prefer broader context before settling on direction.'
    },
    emerging: {
        label: 'Others-Driven',
        emphasis: 'Primary driver',
        summary: 'You tend to process choices more through external views, cues, and surrounding opinion.'
    }
};

const decisionChoiceMeta = {
    high: {
        label: 'Calculated',
        emphasis: 'Primary driver',
        summary: 'You tend to balance multiple aspects before choosing an outcome.'
    },
    medium: {
        label: 'Rational',
        emphasis: 'Primary driver',
        summary: 'Logical thinking tends to drive the way you choose direction.'
    },
    emerging: {
        label: 'Emotional',
        emphasis: 'Primary driver',
        summary: 'People connection and feeling strongly shape the way you choose direction.'
    }
};

const attitudeChoiceMeta = {
    high: {
        label: 'Positive',
        emphasis: 'Foundation',
        summary: 'Your attitude currently acts as a constructive foundation for progress and follow-through.'
    },
    medium: {
        label: 'Neutral',
        emphasis: 'Foundation',
        summary: 'Your attitude foundation is workable, but momentum may fluctuate without reinforcement.'
    },
    emerging: {
        label: 'Negative',
        emphasis: 'Foundation',
        summary: 'Your attitude foundation currently needs extra support, otherwise progress may stall during setbacks.'
    }
};

const resourceChoiceMeta = {
    under: {
        label: 'Under Usage',
        emphasis: 'Execution support',
        summary: 'Available help, tools, and systems may not be getting activated early enough.'
    },
    optimal: {
        label: 'Optimal Usage',
        emphasis: 'Execution support',
        summary: 'Support, tools, and opportunities are being used in a reasonably balanced way.'
    },
    over: {
        label: 'Over Usage',
        emphasis: 'Execution support',
        summary: 'There may be too many inputs or too much reliance on support, so sharper focus is important.'
    }
};

const partBReferenceContent = {
    intro: [
        'One of the crucial questions facing teens is making the choice of a career. The old and often beaten approach of getting on a path that provides a job in the least possible time is the most severely flawed one. It produces a whole lot of youngsters who do not want to walk the path and therefore end up leading a life of underachievement.',
        'You must have noticed engineers becoming financial analysts, or doctors becoming IAS officers, Arts graduates becoming MBA and many becoming entrepreneurs or running their small businesses. All this goes to shows that individuals keep creating their own path depending on the expertise, exposure and education that they get with time.',
        'The top 10 skills mentioned by World Economic Forum for an individual to succeed in future also indicates that other than the technical knowledge and skillset which is a given in today’s information age, the other top skills for success puts focus on developing critical/analytical thinking, creativity, nurturing the intelligence type, being able to take decisions rationally, being confident and resilient – making these of utmost important to succeed in future.',
        'The young adults of today want to choose a career path that provides them with a meaningful life and that really helps them achieve their full potential. Such a path enables them to lead an enriched life while providing them with a sense of achievement.',
        'The Yancha Teens framework is just providing that to the individuals.'
    ],
    rapidModel: {
        title: 'RAPiD Model',
        intro: 'Yancha Teens uses a proprietary framework RAPID framework to evolve the young adults to reach full potential.',
        note: 'The iterative nature of the model helps an individual understand the way the choices are being made and therefore become responsible for the outcome.'
    },
    careerChoice: {
        title: 'Core Concept',
        intro: 'The foundational element is Attitude which is a prerequisite for success, while the career choice making ones are Perspective and Decision Making, and Resources is the element that ensures achievement of intent.',
        note: 'If the foundational element, attitude, is not in place, the higher order ones are immaterial as the individual will find it difficult to reach full potential. And with the foundational element and the Decision Making and Perspectives in place, if the Resources are not deployed well, one lands up with a knowing – doing gap.'
    },
    careerChoices: {
        title: 'Possible Career Choices',
        paragraphs: [
            'Many individuals choose the often chosen road towards a professional program only to realise that they have made a wrong choice and get stuck in it. Some get confused between hobby and profession; and several others limit their options due to guidance of well-wishers.',
            'The Yancha framework assesses on the nineteen different behaviours and gives a comprehensive output. This approach opens the gates for a long lasting pursuit in a particular direction.',
            'An incorrect career choice gives rise to lifelong dissatisfaction, while an appropriate career gives rise to a satisfied life.',
            'Over time as different career options emerge, a behaviourally correct direction enables switching to a more appropriate profession.'
        ],
        note: 'Note: As the output is based on the responses provided by the individual, the respondent is request to select the choices that he or she would do in that situation; not would like to do.'
    },
    pillars: {
        attitude: {
            title: 'Attitude',
            paragraphs: [
                'Your attitude determines your Altitude.',
                'While several people get into a career, those with a positive attitude are the ones who are successful in it. The Yancha Teens attitude indicator, helps define the extent of success that one would get in the field of choice.',
                'Attitude is the approach towards life and ability to overcome setbacks. It can be Positive, Neutral or Negative.',
                'An individual with a positive attitude induces a positive spiral, is liked by one and all and is successful in career. Over time we all face setbacks and the positive attitude is the most essential weapon to have in the suite to overcome them and get on moving with life.'
            ]
        },
        perspectives: {
            title: 'Perspectives',
            paragraphs: [
                'We all understand things our way and our perspectives can be very different than others on the same event being observed. When faced with situations people tend to understand it in three different ways – by doing a deep dive within and trying to draw interpretation; by reaching out to others for insights and going by their guidance or by seeking out new insights beyond what self and others could provide.',
                'Perspectives is the way to analyse and interpret a situation based on information. It can be self-driven, others driven or seek out new.',
                'Certain professions need a way to develop understanding of it and it is important to understand own approach and figure out the way to proceed.'
            ]
        },
        decisionMaking: {
            title: 'Decision Making',
            paragraphs: [
                'Ultimately with all the insights one has to make decisions and make the choices in a timely manner to succeed. This element measures the decision making ability. Often the impact of the decisions made unravel over time and career choice is one of those. As times change the thought of one profession to be practiced over a lifetime also changes.',
                'The behavioural orientation towards decision making helps find out the right path wherein multiple options can emerge.',
                'A calculated decision maker thinks through different aspects and then makes a decision; a rational decision maker makes a choice that is largely logical in nature; while an emotional decision maker selects options that are guided by emotions.',
                'Decision making is about the way to weigh multiple options and make a choice. It can be Calculated, Rational or Emotional.'
            ]
        },
        resources: {
            title: 'Resources',
            paragraphs: [
                'Resources are essential to achieve any outcome. However the fundamental resources of health and time are within the reach of everyone. At the same time availability of excess resources does not translate into success. It is about the utilization of the resource.',
                'Resources can be used optimally, which ensures that it is regenerative in nature; over used that ensures depletion of the resource and loss of productivity with time and under usage that does not use it to its potential. It is important to understand the resource ability and stretch it to deliver performance in a continuous manner, while becoming better with time.',
                'Resources are about the availability and usage items. It can be optimal usage, over-usage or under-usage.'
            ]
        }
    },
    recommended: {
        title: 'Career Options Recommended',
        paragraphs: [
            'At Yancha we believe that Capability of the Individual, when coupled with the Interests, give rise to the opportunities. While the capabilities and the interests are intrinsic to the person, the opportunities are ones that are available in the marketplace.',
            'Misalignment of these can give rise to immense stress. These happen when the individual forcibly chooses a career out of fascination, but is not really cut out for it; or is an individual chooses a particular path because everyone else is going that way, without recognizing the uniqueness in self. The Yancha approach provides the most suited option. It shows a path on which the individual will find success, if working with a positive attitude and with focus on optimal resource utilization - beginning with Self.'
        ]
    }
};

const partBChoiceMatrix = [
    {
        perspective: 'Seek out New',
        cells: {
            Calculated: 'Balances multiple aspects for outcome\nSeeks new information and processes only when everything is available',
            Rational: 'Logical thinking drives outcome\nSeeks new information and processes only when everything is available',
            Emotional: 'Connects with people\nSeeks new information and processes only when everything is available'
        }
    },
    {
        perspective: 'Others-Driven',
        cells: {
            Calculated: 'Balances multiple aspects for outcome\nProcesses based on views of others',
            Rational: 'Logical thinking drives outcome\nProcesses based on views of others',
            Emotional: 'Connects with people\nProcesses based on views of others'
        }
    },
    {
        perspective: 'Self-Driven',
        cells: {
            Calculated: 'Balances multiple aspects for outcome\nProcesses primarily based on insight',
            Rational: 'Logical thinking drives outcome\nProcesses primarily based on insight',
            Emotional: 'Connects with people\nProcesses primarily based on insight'
        }
    }
];

const PART_B_RECOMMENDATION_FALLBACK = 'Score data incomplete. Please contact your Yancha career counsellor.';
const PART_B_RECOMMENDATION_CTA = PART_A_CAREER_COUNSELLOR_MESSAGE;

const PART_B_PERSPECTIVE_INPUT_MAP = {
    'Seek out New': 'Seek New',
    'Seek New': 'Seek New',
    'Others-Driven': 'Others Driven',
    'Others Driven': 'Others Driven',
    'Self-Driven': 'Self Driven',
    'Self Driven': 'Self Driven',
    'Individual Driven': 'Self Driven'
};

const PART_B_DECISION_INPUT_MAP = {
    Calculated: 'Calculated',
    Rational: 'Rational',
    Emotional: 'Emotional'
};

const PART_B_ATTITUDE_INPUT_MAP = {
    Positive: 'Positive',
    Neutral: 'Neutral',
    Negative: 'Negative'
};

const PART_B_RESOURCE_INPUT_MAP = {
    'Optimal Usage': 'Optimal',
    Optimal: 'Optimal',
    'Under Usage': 'Under',
    Under: 'Under',
    'Over Usage': 'Over',
    Over: 'Over'
};

const PART_B_CAREER_MAP = {
    'Seek New': {
        Calculated: 'You are naturally suited to entrepreneurial, leadership, and high-stakes commercial roles. You seek new opportunities and make calculated decisions — a combination that drives business building, product thinking, and market leadership.',
        Rational: 'You thrive at the intersection of innovation and structured reasoning. Roles in finance, investigative work, process design, and systems innovation align with your ability to explore new ground while staying grounded in logic.',
        Emotional: 'You are drawn to roles where human impact and creative vision intersect. Social entrepreneurship, design thinking, education design, and creative leadership are where your energy finds its highest expression.'
    },
    'Others Driven': {
        Calculated: "You excel in roles that require interpreting the world through others' needs and responding with precision. Consulting, journalism, law enforcement, and judiciary work suit your analytical yet socially-oriented mindset.",
        Rational: 'You are built for roles that require coordination, governance, and systems that serve communities. Public administration, policy making, integration, and operational design are your natural domain.',
        Emotional: 'You thrive in people-facing environments where empathy and responsiveness are core competencies. Service sectors, IT services, hospitality, and urban planning align with how you engage with the world.'
    },
    'Self Driven': {
        Calculated: 'You operate best with independence, discipline, and long-horizon thinking. Research, science, agriculture, banking, defence, and high-stakes field roles suit your self-directed and methodical nature.',
        Rational: 'You are suited to roles requiring deep expertise, precision, and systematic problem solving. Engineering, medicine, architecture, law, farming, and product development are where your capabilities are best applied.',
        Emotional: 'You bring depth, craft, and personal voice to everything you do. Teaching, writing, editing, film direction, performing arts, and culinary arts are roles where your intrinsic motivation and emotional intelligence create lasting impact.'
    }
};

const PART_B_FIT_MAP = {
    Positive: {
        Optimal: 'Your positive attitude and optimal resource usage make this a strong, well-rounded fit.',
        Under: 'Your positive attitude is a strong foundation, though a tendency to underutilise available resources may slow your progress in these roles.',
        Over: 'Your enthusiasm is an asset, but a pattern of over-extending resources could lead to burnout — channelling that energy with more discipline will sharpen your fit.'
    },
    Neutral: {
        Optimal: 'Your resource usage is well-calibrated, though developing a stronger sense of purpose and direction will help you fully unlock these career paths.',
        Under: 'There is potential here, but both mindset and resource utilisation need active development to convert aptitude into outcomes.',
        Over: 'You have the capacity, but without a clearer attitude and more measured resource use, these roles may feel inconsistent or draining.'
    },
    Negative: {
        Optimal: 'Your resource management is a strength, but your current outlook may create friction in these roles. Attitude work is the highest-leverage investment you can make right now.',
        Under: 'These roles are within reach, but both your outlook and resource habits are working against you. Focused development on both fronts is strongly recommended before committing to a path.',
        Over: 'There are real strengths here, but overextension combined with a negative outlook is a high-risk combination. Sustainable progress will require recalibration on both dimensions.'
    }
};

const conventionalCareerTitles = [
    'Educator',
    'Consultant',
    'Researcher / Scientist',
    'Doctor',
    'Engineer',
    'Accountant',
    'Finance',
    'Information Technology',
    'Legal Expert',
    'Entrepreneur',
    'Pilot',
    'Administrative Services'
];

const nonConventionalCareerTitles = [
    'Tour Guide Specialist',
    'Tea Taster',
    'Hospital Administrator',
    'Forensics',
    'Hotel Management',
    'Mega Events Planner',
    'Sports',
    'Airline Cabin Crew',
    'Specialist Photographer',
    'Movie Director',
    'Social Workers',
    'Fashion Designer'
];

const futureCareerTitles = [
    'Genomics',
    'Artificial Intelligence',
    'Robotics',
    'Exploration for Minerals',
    'Climate Technologies',
    'Agriculture in Increasingly Arid Conditions',
    'Genetics Based Medicines',
    'Personalized Cosmetics',
    'Space',
    'Nanomaterials',
    'Ocean Science',
    'Carbon Science',
    'Artificial Diamonds'
];

export const careerLandscapeGroups = [
    {
        key: 'Conventional',
        title: 'Conventional',
        description: 'Established routes with visible qualifications, structure, and steady professional progression.',
        items: conventionalCareerTitles
    },
    {
        key: 'Non-Conventional',
        title: 'Non-Conventional',
        description: 'Expansive routes that often blend people, creativity, service, experience, and adaptability.',
        items: nonConventionalCareerTitles
    },
    {
        key: 'Future Careers',
        title: 'Future Careers',
        description: 'Emerging opportunity spaces shaped by dynamic science, sustainability, and future capability.',
        items: futureCareerTitles
    }
];

const careerCatalog = [
    { title: 'Educator', group: 'Conventional', focus: 'guiding learning and helping other people grow', riasec: ['S', 'A', 'I'], rapd: ['P', 'D'] },
    { title: 'Consultant', group: 'Conventional', focus: 'solving real client problems through analysis and communication', riasec: ['E', 'I', 'S'], rapd: ['P', 'D'] },
    { title: 'Researcher / Scientist', group: 'Conventional', focus: 'deep inquiry, evidence, and disciplined discovery', riasec: ['I', 'R', 'C'], rapd: ['P', 'R'] },
    { title: 'Doctor', group: 'Conventional', focus: 'combining science, responsibility, and human care', riasec: ['S', 'I', 'R'], rapd: ['P', 'D'] },
    { title: 'Engineer', group: 'Conventional', focus: 'building systems, solutions, and reliable applied outcomes', riasec: ['R', 'I', 'C'], rapd: ['R', 'D'] },
    { title: 'Accountant', group: 'Conventional', focus: 'accuracy, structure, and dependable financial order', riasec: ['C', 'I', 'E'], rapd: ['D', 'R'] },
    { title: 'Finance', group: 'Conventional', focus: 'evaluating trade-offs, numbers, and strategic value', riasec: ['C', 'E', 'I'], rapd: ['D', 'P'] },
    { title: 'Information Technology', group: 'Conventional', focus: 'solving digital problems through systems, logic, and implementation', riasec: ['I', 'C', 'R'], rapd: ['R', 'D'] },
    { title: 'Legal Expert', group: 'Conventional', focus: 'weighing evidence, structure, and persuasive reasoning', riasec: ['E', 'C', 'S'], rapd: ['P', 'D'] },
    { title: 'Entrepreneur', group: 'Conventional', focus: 'turning ideas into ventures and opportunities', riasec: ['E', 'R', 'I'], rapd: ['A', 'D'] },
    { title: 'Pilot', group: 'Conventional', focus: 'precision, responsibility, and composed action in motion', riasec: ['R', 'C', 'E'], rapd: ['D', 'R'] },
    { title: 'Administrative Services', group: 'Conventional', focus: 'managing systems, people, and operations smoothly', riasec: ['C', 'E', 'S'], rapd: ['D', 'P'] },
    { title: 'Tour Guide Specialist', group: 'Non-Conventional', focus: 'experience design, storytelling, and engaging people in place', riasec: ['S', 'E', 'A'], rapd: ['A', 'P'] },
    { title: 'Tea Taster', group: 'Non-Conventional', focus: 'sensory judgment, nuance, and refined evaluation', riasec: ['A', 'I', 'C'], rapd: ['P', 'A'] },
    { title: 'Hospital Administrator', group: 'Non-Conventional', focus: 'balancing care systems, people, and operations', riasec: ['C', 'S', 'E'], rapd: ['R', 'D'] },
    { title: 'Forensics', group: 'Non-Conventional', focus: 'investigation, evidence, and disciplined problem solving', riasec: ['I', 'C', 'R'], rapd: ['P', 'D'] },
    { title: 'Hotel Management', group: 'Non-Conventional', focus: 'service leadership, coordination, and guest experience', riasec: ['S', 'E', 'C'], rapd: ['A', 'R'] },
    { title: 'Mega Events Planner', group: 'Non-Conventional', focus: 'orchestrating experiences through planning, creativity, and execution', riasec: ['E', 'A', 'S'], rapd: ['R', 'D'] },
    { title: 'Sports', group: 'Non-Conventional', focus: 'performance, discipline, competition, and visible action', riasec: ['R', 'E', 'S'], rapd: ['A', 'D'] },
    { title: 'Airline Cabin Crew', group: 'Non-Conventional', focus: 'service under pressure with composure and coordination', riasec: ['S', 'E', 'C'], rapd: ['A', 'R'] },
    { title: 'Specialist Photographer', group: 'Non-Conventional', focus: 'capturing meaning through craft, observation, and visual expression', riasec: ['A', 'R', 'I'], rapd: ['P', 'A'] },
    { title: 'Movie Director', group: 'Non-Conventional', focus: 'leading creative vision, people, and storytelling decisions', riasec: ['A', 'E', 'S'], rapd: ['P', 'D'] },
    { title: 'Social Workers', group: 'Non-Conventional', focus: 'supporting people and communities through empathy and action', riasec: ['S', 'E', 'A'], rapd: ['A', 'P'] },
    { title: 'Fashion Designer', group: 'Non-Conventional', focus: 'turning originality into visible style and crafted expression', riasec: ['A', 'E', 'C'], rapd: ['P', 'R'] },
    { title: 'Genomics', group: 'Future Careers', focus: 'future-facing biological discovery through precision and inquiry', riasec: ['I', 'R', 'C'], rapd: ['P', 'R'] },
    { title: 'Artificial Intelligence', group: 'Future Careers', focus: 'pattern recognition, systems thinking, and applied intelligence', riasec: ['I', 'C', 'R'], rapd: ['P', 'D'] },
    { title: 'Robotics', group: 'Future Careers', focus: 'integrating hardware, systems, and intelligent design', riasec: ['R', 'I', 'C'], rapd: ['R', 'D'] },
    { title: 'Exploration for Minerals', group: 'Future Careers', focus: 'applied science and field-based discovery for resource futures', riasec: ['R', 'I', 'C'], rapd: ['R', 'D'] },
    { title: 'Climate Technologies', group: 'Future Careers', focus: 'solving urgent sustainability problems with science and purpose', riasec: ['I', 'R', 'S'], rapd: ['A', 'P'] },
    { title: 'Agriculture in Increasingly Arid Conditions', group: 'Future Careers', focus: 'adapting food systems through practical science and resilience', riasec: ['R', 'I', 'S'], rapd: ['A', 'R'] },
    { title: 'Genetics Based Medicines', group: 'Future Careers', focus: 'blending science, care, and precision health', riasec: ['I', 'S', 'R'], rapd: ['P', 'D'] },
    { title: 'Personalized Cosmetics', group: 'Future Careers', focus: 'merging design, science, and individual experience', riasec: ['A', 'I', 'E'], rapd: ['P', 'R'] },
    { title: 'Space', group: 'Future Careers', focus: 'frontier exploration, systems thinking, and disciplined ambition', riasec: ['I', 'R', 'C'], rapd: ['D', 'R'] },
    { title: 'Nanomaterials', group: 'Future Careers', focus: 'precision innovation through material science and experimentation', riasec: ['I', 'R', 'C'], rapd: ['P', 'D'] },
    { title: 'Ocean Science', group: 'Future Careers', focus: 'exploring living systems, environment, and planetary change', riasec: ['I', 'R', 'S'], rapd: ['A', 'P'] },
    { title: 'Carbon Science', group: 'Future Careers', focus: 'future sustainability through evidence, systems, and problem solving', riasec: ['I', 'R', 'C'], rapd: ['P', 'R'] },
    { title: 'Artificial Diamonds', group: 'Future Careers', focus: 'advanced materials, process discipline, and innovation', riasec: ['I', 'C', 'R'], rapd: ['R', 'D'] }
];

const bandLabels = {
    high: 'Strong',
    medium: 'Developing',
    emerging: 'Emerging'
};

const behaviorBandLabels = {
    strong: 'Ready and future-facing',
    developing: 'Developing steady momentum',
    emerging: 'Emerging with support'
};

const archetypeAdjectives = {
    R: 'Grounded',
    I: 'Analytical',
    A: 'Creative',
    S: 'People-First',
    E: 'Enterprising',
    C: 'Structured'
};

const archetypeNouns = {
    R: 'Builder',
    I: 'Explorer',
    A: 'Creator',
    S: 'Guide',
    E: 'Catalyst',
    C: 'Planner'
};

const rapdAdjectives = {
    A: 'Adaptive',
    P: 'Reflective',
    R: 'Resourceful',
    D: 'Deliberate'
};

const groupTrajectory = {
    Conventional: 'This is a structured route where qualifications, disciplined capability, and consistency tend to open doors.',
    'Non-Conventional': 'This is an expansive route where experience, people skills, originality, and adaptability can create distinct opportunities.',
    'Future Careers': 'This is a future-facing route where curiosity, continuous learning, and dynamic problem solving matter deeply.'
};

const roundScore = (value) => Math.round(value * 10) / 10;
const toPercent = (value) => Math.round(value * 10);
const pickRandomItem = (items = []) => (items.length ? items[Math.floor(Math.random() * items.length)] : null);

const getRapdBand = (score) => {
    if (score >= 7) return 'high';
    if (score >= 4) return 'medium';
    return 'emerging';
};

const getResourceUsageKey = (score) => {
    if (score >= 7) return 'over';
    if (score >= 4) return 'optimal';
    return 'under';
};

const getMaxScoreMap = (questions = [], codes = [], multiplier = 1) => {
    return codes.reduce((accumulator, code) => {
        const matchingQuestions = Array.isArray(questions)
            ? questions.filter((question) => question.type === code).length
            : 0;

        const safeCount = matchingQuestions > 0 ? matchingQuestions : 1;
        accumulator[code] = safeCount * multiplier;
        return accumulator;
    }, {});
};

const rankEntries = (entries = []) => {
    const sorted = [...entries].sort((left, right) => right.score - left.score);
    const rankMap = sorted.reduce((accumulator, item, index) => {
        accumulator[item.code] = index + 1;
        return accumulator;
    }, {});

    return {
        ordered: entries.map((item) => ({ ...item, rank: rankMap[item.code] })),
        ranked: sorted.map((item) => ({ ...item, rank: rankMap[item.code] }))
    };
};

const buildCareerNarrative = (career, dominantOrientation, secondaryOrientation, behaviorLead, behaviorSupport) => {
    return `${career.title} stands out because it rewards ${career.focus}. That fits well with your ${dominantOrientation.title.toLowerCase()} orientation, the support of ${secondaryOrientation.title.toLowerCase()} interests, and the way ${behaviorLead.title.toLowerCase()} plus ${behaviorSupport.title.toLowerCase()} can carry your interest into action.`;
};

const determineOutcome = (dominantOrientation, rapdRanked) => {
    const interestIsClear = dominantOrientation.score >= 7;
    const highCount = rapdRanked.filter((item) => item.band === 'high').length;
    const mediumOrHighCount = rapdRanked.filter((item) => item.band !== 'emerging').length;

    let behaviorStrength = 'emerging';

    if (highCount >= 2 || (highCount >= 1 && mediumOrHighCount >= 3)) {
        behaviorStrength = 'strong';
    } else if (mediumOrHighCount >= 2) {
        behaviorStrength = 'developing';
    }

    let outcomeKey = 'support_oriented';

    if (behaviorStrength === 'emerging') {
        outcomeKey = 'support_oriented';
    } else if (interestIsClear && behaviorStrength === 'strong') {
        outcomeKey = 'aligned_resilient';
    } else if (interestIsClear && behaviorStrength === 'developing') {
        outcomeKey = 'aligned_developing';
    } else if (!interestIsClear && behaviorStrength === 'strong') {
        outcomeKey = 'misaligned_capable';
    } else if (!interestIsClear && behaviorStrength === 'developing') {
        outcomeKey = 'exploratory_unsettled';
    }

    return {
        interestIsClear,
        behaviorStrength,
        behaviorStrengthLabel: behaviorBandLabels[behaviorStrength],
        outcomeKey,
        outcome: outcomesData.quadrant_outcomes[outcomeKey]
    };
};

const buildResolvedChoice = (entry, meta) => ({
    code: entry.code,
    pillar: entry.title,
    descriptor: entry.descriptor,
    label: meta.label,
    emphasis: meta.emphasis,
    summary: meta.summary,
    score: entry.score,
    percent: entry.percent,
    band: entry.band,
    bandLabel: entry.bandLabel,
    color: entry.color
});

const buildPartBRecommendation = ({
    perspectives: perspectivesChoice,
    decision: decisionChoice,
    attitude: attitudeChoice,
    resources: resourcesChoice
}) => {
    const perspectiveKey = PART_B_PERSPECTIVE_INPUT_MAP[perspectivesChoice.label];
    const decisionKey = PART_B_DECISION_INPUT_MAP[decisionChoice.label];
    const attitudeKey = PART_B_ATTITUDE_INPUT_MAP[attitudeChoice.label];
    const resourceKey = PART_B_RESOURCE_INPUT_MAP[resourcesChoice.label];

    const careerNarrative = perspectiveKey && decisionKey
        ? PART_B_CAREER_MAP[perspectiveKey]?.[decisionKey]
        : null;

    const fitLine = attitudeKey && resourceKey
        ? PART_B_FIT_MAP[attitudeKey]?.[resourceKey]
        : null;

    if (!perspectiveKey || !decisionKey || !attitudeKey || !resourceKey || !careerNarrative || !fitLine) {
        return {
            narrative: PART_B_RECOMMENDATION_FALLBACK,
            fitLine: '',
            cta: PART_B_RECOMMENDATION_CTA,
            paragraphs: [PART_B_RECOMMENDATION_FALLBACK],
            summary: PART_B_RECOMMENDATION_FALLBACK,
            matrixKey: null
        };
    }

    return {
        narrative: careerNarrative,
        fitLine,
        cta: PART_B_RECOMMENDATION_CTA,
        paragraphs: [careerNarrative, fitLine],
        summary: `${careerNarrative} ${fitLine} ${PART_B_RECOMMENDATION_CTA}`,
        matrixKey: `${perspectiveKey} × ${decisionKey}`
    };
};

const buildPartATraitReport = (topTraits = []) => topTraits.map((entry) => {
    const inference = partATraitInference[entry.code];

    return {
        ...entry,
        heading: inference.heading,
        careerHeading: inference.careerHeading,
        paragraphs: inference.paragraphs,
        careerIntro: `Some of the occupational areas that fit the ${entry.title.toLowerCase()} personality type are:`,
        careers: entry.careerPool || [],
        counsellorMessage: PART_A_CAREER_COUNSELLOR_MESSAGE
    };
});

const buildCareerMatches = (riasecRanked, rapdRanked) => {
    const dominantOrientation = riasecRanked[0];
    const secondaryOrientation = riasecRanked[1];
    const thirdOrientation = riasecRanked[2];
    const behaviorLead = rapdRanked[0];
    const behaviorSupport = rapdRanked[1];

    const riasecRankIndex = riasecRanked.reduce((accumulator, item, index) => {
        accumulator[item.code] = index;
        return accumulator;
    }, {});

    const riasecScoreMap = riasecRanked.reduce((accumulator, item) => {
        accumulator[item.code] = item.score;
        return accumulator;
    }, {});

    const rapdScoreMap = rapdRanked.reduce((accumulator, item) => {
        accumulator[item.code] = item.score;
        return accumulator;
    }, {});

    const rapdBandMap = rapdRanked.reduce((accumulator, item) => {
        accumulator[item.code] = item.band;
        return accumulator;
    }, {});

    const futureTilt = ['I', 'R'].includes(dominantOrientation.code) || ['I', 'R'].includes(secondaryOrientation.code);
    const peopleCreativeTilt = ['A', 'S', 'E'].includes(dominantOrientation.code) || ['A', 'S', 'E'].includes(secondaryOrientation.code);
    const structuredTilt = ['C', 'I', 'R'].includes(dominantOrientation.code) || ['D', 'R'].includes(behaviorLead.code);

    const scoredCareers = careerCatalog
        .map((career) => {
            let matchScore = 0;

            career.riasec.forEach((code, index) => {
                const rank = riasecRankIndex[code];

                if (rank === 0) {
                    matchScore += 6 - index;
                } else if (rank === 1) {
                    matchScore += 4 - index * 0.5;
                } else if (rank === 2) {
                    matchScore += 2;
                }

                matchScore += (riasecScoreMap[code] || 0) * 0.22;
            });

            career.rapd.forEach((code) => {
                const band = rapdBandMap[code];

                if (band === 'high') {
                    matchScore += 3.4;
                } else if (band === 'medium') {
                    matchScore += 1.7;
                }

                matchScore += (rapdScoreMap[code] || 0) * 0.12;
            });

            if (career.group === 'Future Careers' && futureTilt) {
                matchScore += 2.2;
            }

            if (career.group === 'Non-Conventional' && peopleCreativeTilt) {
                matchScore += 1.3;
            }

            if (career.group === 'Conventional' && structuredTilt) {
                matchScore += 1.3;
            }

            if (career.group === 'Future Careers' && thirdOrientation.code === 'I') {
                matchScore += 0.6;
            }

            return {
                ...career,
                matchScore: roundScore(matchScore),
                trajectory: groupTrajectory[career.group],
                why: buildCareerNarrative(career, dominantOrientation, secondaryOrientation, behaviorLead, behaviorSupport)
            };
        })
        .sort((left, right) => right.matchScore - left.matchScore);

    const primary = scoredCareers[0];
    const optionSpotlight = scoredCareers.find((item) => item.group !== 'Future Careers') || primary;
    const futureSpotlight = scoredCareers.find((item) => item.group === 'Future Careers') || primary;

    const reservedTitles = new Set([primary.title, optionSpotlight.title, futureSpotlight.title]);

    const supporting = scoredCareers.filter((item) => !reservedTitles.has(item.title)).slice(0, 4);

    const highlightedTitles = new Set([
        primary.title,
        optionSpotlight.title,
        futureSpotlight.title,
        ...supporting.map((item) => item.title)
    ]);

    const landscapeGroups = careerLandscapeGroups.map((group) => ({
        ...group,
        items: group.items.map((title) => ({
            title,
            highlighted: highlightedTitles.has(title),
            primary: primary.title === title,
            optionSpotlight: optionSpotlight.title === title,
            futureSpotlight: futureSpotlight.title === title
        }))
    }));

    return {
        primary,
        optionSpotlight,
        futureSpotlight,
        supporting,
        landscapeGroups
    };
};

export const buildFullCareerReport = ({ scores = {}, quizData = {} }) => {
    const maxScoresA = getMaxScoreMap(quizData.parta, RIASEC_ORDER, 1);
    const maxScoresB = getMaxScoreMap(quizData.partb, RAPD_ORDER, 3);

    const rawPartA = scores.parta || {};
    const rawPartB = scores.partb || {};

    const riasecBaseEntries = RIASEC_ORDER.map((code) => {
        const maxScore = maxScoresA[code] || 1;
        const score = Math.min(((rawPartA[code] || 0) / maxScore) * 10, 10);
        const careerPool = partACareerPools[code] || [];

        return {
            code,
            ...riasecMeta[code],
            score: roundScore(score),
            percent: toPercent(score),
            rawScore: rawPartA[code] || 0,
            maxScore,
            careerPool,
            careerExample: pickRandomItem(careerPool)
        };
    });

    const rapdBaseEntries = RAPD_ORDER.map((code) => {
        const maxScore = maxScoresB[code] || 3;
        const score = Math.min(((rawPartB[code] || 0) / maxScore) * 10, 10);
        const band = getRapdBand(score);

        return {
            code,
            ...rapdMeta[code],
            score: roundScore(score),
            percent: toPercent(score),
            rawScore: rawPartB[code] || 0,
            maxScore,
            band,
            bandLabel: bandLabels[band],
            bandText: rapdMeta[code].bands[band]
        };
    });

    const { ordered: riasecEntries, ranked: riasecRanked } = rankEntries(riasecBaseEntries);
    const { ordered: rapdEntries, ranked: rapdRanked } = rankEntries(rapdBaseEntries);

    const dominantOrientation = riasecRanked[0];
    const secondaryOrientation = riasecRanked[1];
    const tertiaryOrientation = riasecRanked[2];
    const behaviorLead = rapdRanked[0];
    const behaviorSupport = rapdRanked[1];
    const lowestRapdPillar = rapdRanked[rapdRanked.length - 1];
    const attitudeEntry = rapdEntries.find((item) => item.code === 'A');
    const perspectivesEntry = rapdEntries.find((item) => item.code === 'P');
    const resourcesEntry = rapdEntries.find((item) => item.code === 'R');
    const decisionEntry = rapdEntries.find((item) => item.code === 'D');

    const outcomeInfo = determineOutcome(dominantOrientation, rapdRanked);
    const careers = buildCareerMatches(riasecRanked, rapdRanked);
    const partBResolvedChoices = {
        perspectives: buildResolvedChoice(perspectivesEntry, perspectiveChoiceMeta[perspectivesEntry.band]),
        decision: buildResolvedChoice(decisionEntry, decisionChoiceMeta[decisionEntry.band]),
        attitude: buildResolvedChoice(attitudeEntry, attitudeChoiceMeta[attitudeEntry.band]),
        resources: buildResolvedChoice(resourcesEntry, resourceChoiceMeta[getResourceUsageKey(resourcesEntry.score)])
    };
    const partBRecommendation = buildPartBRecommendation(partBResolvedChoices);

    const partASummary = `${dominantOrientation.title} is your strongest orientation right now, with ${secondaryOrientation.title} close behind. This usually points to environments where you can ${dominantOrientation.motivation} while also finding room to ${secondaryOrientation.motivation}.`;

    const partAEnvironmentSummary = `You are likely to feel most engaged in spaces that combine ${dominantOrientation.environment} with ${secondaryOrientation.environment}.`;

    const partAMotivationSummary = `Your strongest motivation signal is the chance to ${dominantOrientation.motivation}. ${secondaryOrientation.title} adds a second layer by helping you stay interested when work also lets you ${secondaryOrientation.motivation}.`;
    const partATopTraits = buildPartATraitReport([dominantOrientation, secondaryOrientation, tertiaryOrientation]);

    const partBSummary = `Your Part B responses are led by ${behaviorLead.title} and ${behaviorSupport.title}. Together, they suggest a ${outcomeInfo.behaviorStrengthLabel.toLowerCase()} behavioural base that can shape how confidently and thoughtfully you move from interest into action.`;
    const partBLiveSummary = `Your Part B profile currently resolves as ${partBResolvedChoices.perspectives.label} in Perspectives, ${partBResolvedChoices.decision.label} in Decision Making, ${partBResolvedChoices.attitude.label} in Attitude, and ${partBResolvedChoices.resources.label} in Resources.`;

    const archetypeTitle = `${archetypeAdjectives[dominantOrientation.code]} ${rapdAdjectives[behaviorLead.code]} ${archetypeNouns[dominantOrientation.code]}`;

    const synthesisSummary = `You appear most energised when ${dominantOrientation.title.toLowerCase()} interests are given real expression, and you gain the most traction when ${behaviorLead.title.toLowerCase()} guides your next move. This combination makes ${careers.primary.title} a strong spotlight path while keeping ${careers.futureSpotlight.title} in view as an exciting future-facing extension.`;

    const nextSteps = [
        {
            title: 'Build exposure before locking a path',
            text: `Explore ${careers.optionSpotlight.title}${careers.futureSpotlight.title !== careers.optionSpotlight.title ? ` and ${careers.futureSpotlight.title}` : ''} through conversations, observation, projects, or real examples so your choices grow from lived exposure rather than assumption.`
        },
        {
            title: `Strengthen ${lowestRapdPillar.title}`,
            text: lowestRapdPillar.growthPrompt
        },
        {
            title: 'Turn interest into visible capability',
            text: `Choose one activity, portfolio task, club, or challenge that allows your ${dominantOrientation.title.toLowerCase()} interests to become a visible skill over time.`
        }
    ];

    const liveActionPlan = [
        {
            title: 'Validate the Part B recommendation in real settings',
            text: 'Explore these possible career choices through conversations, observation, or small projects so this direction becomes something you can test in the real world.'
        },
        {
            title: `Strengthen ${lowestRapdPillar.title}`,
            text: lowestRapdPillar.growthPrompt
        },
        {
            title: `Turn ${dominantOrientation.title} into visible experience`,
            text: `Choose one activity, challenge, portfolio task, or club that lets your ${dominantOrientation.title.toLowerCase()} orientation become a visible capability over time.`
        }
    ];

    return {
        riasecEntries,
        riasecRanked,
        rapdEntries,
        rapdRanked,
        dominantOrientation,
        secondaryOrientation,
        tertiaryOrientation,
        behaviorLead,
        behaviorSupport,
        lowestRapdPillar,
        interestCode: riasecRanked.slice(0, 3).map((item) => item.code).join('-'),
        outcome: outcomeInfo.outcome,
        behaviorStrengthLabel: outcomeInfo.behaviorStrengthLabel,
        careers,
        partA: {
            intro: partAIntroContent,
            topTraits: partATopTraits,
            counsellorMessage: PART_A_CAREER_COUNSELLOR_MESSAGE,
            summary: partASummary,
            environmentSummary: partAEnvironmentSummary,
            motivationSummary: partAMotivationSummary
        },
        partB: {
            reference: partBReferenceContent,
            choiceMatrix: partBChoiceMatrix,
            summary: partBSummary,
            liveSummary: partBLiveSummary,
            engineNote: 'Perspectives × Decision Making drive the recommendation. Attitude is the foundation. Resources influence how well intent becomes action.',
            resolvedChoices: partBResolvedChoices,
            recommendation: partBRecommendation
        },
        synthesis: {
            archetypeTitle,
            formula: `${dominantOrientation.title} interest + ${behaviorLead.title} + ${behaviorSupport.title} = ${archetypeTitle}.`,
            summary: synthesisSummary,
            nextSteps,
            careerBuilderNote: 'Career is not a single job to be chosen once. It is a path built as interests, capabilities, and opportunities keep interacting over time.'
        },
        liveActionPlan,
        footerNote: outcomesData.footer_note
    };
};
